import { makeAutoObservable } from 'mobx';
import { ICredentialsStore } from '../../../integration-store';
import IntegrationsService from '../../../../../../../services/api/integrations-service';
import { TYPE, rootContainer } from '../../../../../../../services/container/root';

export interface ICanadaPostOAuthCredentials {
    language: string;
    token_id: string;
    platform_id: string;
    return_url: string;
}

export interface ICanadaPostMerchantCredentials {
    merchant_username: string;
    merchant_password: string;
    customer_number: string;
}

interface IEventMessage {
    type: string;
    [payloadKey: string]: any;
}

const CLOSE_INTERVAL = 50;

class CanadaPostOauthCredentialsStore implements ICredentialsStore {
    private _window = {} as Window;
    private _intervalId: any = undefined;
    private _messageHandler?: (event: IEventMessage) => void;

    constructor() {
        makeAutoObservable(this);
    }

    private get IntegrationsService(): IntegrationsService {
        return rootContainer.get<IntegrationsService>(TYPE.IntegrationsService);
    }

    // Handle case when user didn't finish authorization flow and closed window
    private _listenToWindowClosed(reject: (reason?: any) => void) {
        /** make sure to always clear interval before starting new one */
        clearInterval(this._intervalId);

        this._intervalId = setInterval(() => {
            if (!this._window.closed) {
                return;
            }
            clearInterval(this._intervalId);
            reject(new Error('Authorization interrupted, window was closed before finishing the process'));
        }, CLOSE_INTERVAL);
    }

    private async _handleMessage(event: IEventMessage, resolve: (value: unknown) => void, reject: (reason?: any) => void, internalName: string, tokenId: string) {
        if (event.data?.type !== 'CANADA_POST_OAUTH_RESPONSE') {
            return;
        }

        this._window.close();
        clearInterval(this._intervalId);
        window.removeEventListener('message', this._messageHandler as (event: IEventMessage) => void, false);

        if (event.data?.payload?.status === 'SUCCESS') {
            try {
                const credentials = await this.fetchMerchantCredentials(tokenId);
                return resolve({
                    api_key: credentials.merchant_username,
                    api_secret: credentials.merchant_password,
                    customer_number: credentials.customer_number,
                    internal_name: internalName
                });
            }
            catch (e) {
                reject(e);                
            }
        }

        return reject(new Error('Failed to receive credentials'));
    }

    /** ICredentialsStore implementation ************/
    public async getCredentials() {
        const oauthCredentials = await this.fetchOAuthCredentials();

        const searchParams = new URLSearchParams();
        searchParams.append('token-id', oauthCredentials.token_id);
        searchParams.append('platform-id', oauthCredentials.platform_id);
        searchParams.append('language', oauthCredentials.language);
        searchParams.append('return-url', oauthCredentials.return_url);
        const url = 'https://www.canadapost-postescanada.ca/information/app/drc/merchant?' + searchParams.toString();

        if (this._messageHandler) {
            window.removeEventListener('message', this._messageHandler, false);
        }

        return new Promise((resolve, reject) => {
            this._window = window.open(url, '', 'width=800,height=600,resizeable,scrollbars') as Window;

            this._messageHandler = (event: IEventMessage) => {
                this._handleMessage(event, 
                    resolve, 
                    reject, 
                    'Canada Post',
                    oauthCredentials.token_id
                );
            };

            window.addEventListener('message', this._messageHandler, false);

            this._listenToWindowClosed(reject);
        });
    }

    private async fetchOAuthCredentials() {
        return await this.IntegrationsService.getCanadaPostOauthCredentials();
    }

    private async fetchMerchantCredentials(tokenId: string): Promise<ICanadaPostMerchantCredentials> {
        if (!tokenId) {
            throw Error('Token Id is not loaded.');
        }
        return await this.IntegrationsService.getCanadaPostMerchantCredentials(tokenId);
    }
}

export default CanadaPostOauthCredentialsStore;
